import { format, isValid, parse } from 'date-fns';
import { type Locale } from 'date-fns/locale';

/**
 * regular expression to check for valid hour format (01-23)
 */
const isValidHour = (value: string) => {
  return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
};

/**
 * regular expression to check for valid 12 hour format (01-12)
 */
const isValid12Hour = (value: string) => {
  return /^(0[1-9]|1[0-2])$/.test(value);
};

/**
 * regular expression to check for valid minute format (00-59)
 */
const isValidMinuteOrSecond = (value: string) => {
  return /^[0-5][0-9]$/.test(value);
};

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean };

const getValidNumber = (value: string, { max, min = 0, loop = false }: GetValidNumberConfig) => {
  let numericValue = parseInt(value, 10);

  if (!Number.isNaN(numericValue)) {
    if (!loop) {
      if (numericValue > max) {
        numericValue = max;
      }
      if (numericValue < min) {
        numericValue = min;
      }
    } else {
      if (numericValue > max) {
        numericValue = min;
      }
      if (numericValue < min) {
        numericValue = max;
      }
    }
    return numericValue.toString().padStart(2, '0');
  }

  return '00';
};

const getValidHour = (value: string) => {
  if (isValidHour(value)) {
    return value;
  }
  return getValidNumber(value, { max: 23 });
};

const getValid12Hour = (value: string) => {
  if (isValid12Hour(value)) {
    return value;
  }
  return getValidNumber(value, { min: 1, max: 12 });
};

const getValidMinuteOrSecond = (value: string) => {
  if (isValidMinuteOrSecond(value)) {
    return value;
  }
  return getValidNumber(value, { max: 59 });
};

type GetValidArrowNumberConfig = {
  min: number;
  max: number;
  step: number;
};

const getValidArrowNumber = (value: string, { min, max, step }: GetValidArrowNumberConfig) => {
  let numericValue = parseInt(value, 10);
  if (!Number.isNaN(numericValue)) {
    numericValue += step;
    return getValidNumber(String(numericValue), { min, max, loop: true });
  }
  return '00';
};

const getValidArrowHour = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 0, max: 23, step });
};

const getValidArrow12Hour = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 1, max: 12, step });
};

const getValidArrowMinuteOrSecond = (value: string, step: number) => {
  return getValidArrowNumber(value, { min: 0, max: 59, step });
};

const setMinutes = (date: Date, value: string) => {
  const minutes = getValidMinuteOrSecond(value);
  date.setMinutes(parseInt(minutes, 10));
  return date;
};

const setSeconds = (date: Date, value: string) => {
  const seconds = getValidMinuteOrSecond(value);
  date.setSeconds(parseInt(seconds, 10));
  return date;
};

const setHours = (date: Date, value: string) => {
  const hours = getValidHour(value);
  date.setHours(parseInt(hours, 10));
  return date;
};

const set12Hours = (date: Date, value: string, period: Period) => {
  const hours = parseInt(getValid12Hour(value), 10);
  const convertedHours = convert12HourTo24Hour(hours, period);
  date.setHours(convertedHours);
  return date;
};

export enum TimePickerType {
  Minutes = 'minutes',
  Seconds = 'seconds',
  Hours = 'hours',
  TwelveHours = '12hours',
}
export enum Period {
  AM = 'AM',
  PM = 'PM',
}

export enum DateTimeGranularity {
  Day = 'day',
  Hour = 'hour',
  Minute = 'minute',
  Second = 'second',
}

type DateTimeInputConfig = {
  format: string;
  mask: string;
  placeholder: string;
  length: number;
};

export const getDateTimeInputConfig = (granularity: DateTimeGranularity): DateTimeInputConfig => {
  switch (granularity) {
    case DateTimeGranularity.Day:
      return {
        format: 'yyyy-MM-dd',
        mask: '9999-99-99',
        placeholder: 'YYYY-MM-DD',
        length: 10,
      };
    case DateTimeGranularity.Hour:
      return {
        format: 'yyyy-MM-dd HH',
        mask: '9999-99-99 99',
        placeholder: 'YYYY-MM-DD HH',
        length: 13,
      };
    case DateTimeGranularity.Minute:
      return {
        format: 'yyyy-MM-dd HH:mm',
        mask: '9999-99-99 99:99',
        placeholder: 'YYYY-MM-DD HH:MM',
        length: 16,
      };
    case DateTimeGranularity.Second:
    default:
      return {
        format: 'yyyy-MM-dd HH:mm:ss',
        mask: '9999-99-99 99:99:99',
        placeholder: 'YYYY-MM-DD HH:MM:SS',
        length: 19,
      };
  }
};

export const parseDateTimeInput = (value: string, granularity: DateTimeGranularity) => {
  const { format: inputFormat, length } = getDateTimeInputConfig(granularity);

  if (value.length !== length) {
    return undefined;
  }

  const parsedDate = parse(value, inputFormat, new Date());
  if (!isValid(parsedDate)) {
    return undefined;
  }

  if (format(parsedDate, inputFormat) !== value) {
    return undefined;
  }

  return parsedDate;
};

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate();
};

type DateTimeValidationResult = { valid: true } | { valid: false; message: string };

type DateValidationOptions = { minYear?: number; maxYear?: number; yearRange?: number };
type DateTimeValidationOptions = DateValidationOptions & {
  granularity?: DateTimeGranularity;
  hourCycle?: 12 | 24;
};

export const DEFAULT_YEAR_RANGE = 50;

export const getDefaultYearRange = (yearRange = DEFAULT_YEAR_RANGE) => {
  const currentYear = new Date().getFullYear();
  return { minYear: currentYear - yearRange, maxYear: currentYear + yearRange };
};

const resolveYearRange = (options: DateValidationOptions = {}) => {
  const defaultRange = getDefaultYearRange(options.yearRange);
  let minYear = options.minYear ?? defaultRange.minYear;
  let maxYear = options.maxYear ?? defaultRange.maxYear;

  if (minYear > maxYear) {
    [minYear, maxYear] = [maxYear, minYear];
  }

  return { minYear, maxYear };
};

const buildRangeMessage = (label: string, min: number, max: number) =>
  `${label} must be between ${String(min).padStart(2, '0')} and ${String(max).padStart(2, '0')}.`;

const buildYearRangeMessage = (minYear: number, maxYear: number) => `Year must be between ${minYear} and ${maxYear}.`;

const validateYearRange = (year: number, options: DateValidationOptions) => {
  const { minYear, maxYear } = resolveYearRange(options);

  if (year < minYear || year > maxYear) {
    return { valid: false, message: buildYearRangeMessage(minYear, maxYear) } as const;
  }

  return { valid: true } as const;
};

const validateDateParts = (year: number, month: number, day: number, options: DateValidationOptions) => {
  const yearResult = validateYearRange(year, options);
  if (!yearResult.valid) {
    return yearResult;
  }

  if (month < 1 || month > 12) {
    return { valid: false, message: buildRangeMessage('Month', 1, 12) } as const;
  }

  const maxDays = getDaysInMonth(year, month);
  if (day < 1 || day > maxDays) {
    return { valid: false, message: buildRangeMessage('Day', 1, maxDays) } as const;
  }

  return { valid: true } as const;
};

const validateHour = (hour: number, hourCycle: 12 | 24) => {
  const minHour = hourCycle === 12 ? 1 : 0;
  const maxHour = hourCycle === 12 ? 12 : 23;

  if (hour < minHour || hour > maxHour) {
    return { valid: false, message: buildRangeMessage('Hours', minHour, maxHour) } as const;
  }

  return { valid: true } as const;
};

const validateMinute = (minute: number) => {
  if (minute < 0 || minute > 59) {
    return { valid: false, message: buildRangeMessage('Minutes', 0, 59) } as const;
  }

  return { valid: true } as const;
};

const validateSecond = (second: number) => {
  if (second < 0 || second > 59) {
    return { valid: false, message: buildRangeMessage('Seconds', 0, 59) } as const;
  }

  return { valid: true } as const;
};

const validateTimeParts = (
  hour: number,
  minute: number,
  second: number,
  options: { granularity: DateTimeGranularity; hourCycle: 12 | 24 },
) => {
  if (options.granularity === DateTimeGranularity.Day) {
    return { valid: true } as const;
  }

  const hourResult = validateHour(hour, options.hourCycle);
  if (!hourResult.valid) {
    return hourResult;
  }

  if (options.granularity === DateTimeGranularity.Hour) {
    return { valid: true } as const;
  }

  const minuteResult = validateMinute(minute);
  if (!minuteResult.valid) {
    return minuteResult;
  }

  if (options.granularity === DateTimeGranularity.Minute) {
    return { valid: true } as const;
  }

  return validateSecond(second);
};

export const validateDateInputString = (
  value: string,
  options: DateValidationOptions = {},
): DateTimeValidationResult => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return {
      valid: false,
      message: `Use format ${getDateTimeInputConfig(DateTimeGranularity.Day).placeholder}.`,
    };
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  return validateDateParts(year, month, day, options);
};

export const validateDateTimeInputString = (
  value: string,
  options: DateTimeValidationOptions = {},
): DateTimeValidationResult => {
  const granularity = options.granularity ?? DateTimeGranularity.Second;
  const hourCycle = options.hourCycle ?? 24;

  if (granularity === DateTimeGranularity.Day) {
    return validateDateInputString(value, options);
  }

  const formatMessage = `Use format ${getDateTimeInputConfig(granularity).placeholder}.`;

  const patterns: Record<DateTimeGranularity, RegExp> = {
    [DateTimeGranularity.Day]: /^(\d{4})-(\d{2})-(\d{2})$/,
    [DateTimeGranularity.Hour]: /^(\d{4})-(\d{2})-(\d{2}) (\d{2})$/,
    [DateTimeGranularity.Minute]: /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/,
    [DateTimeGranularity.Second]: /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
  };

  const match = patterns[granularity].exec(value);
  if (!match) {
    return { valid: false, message: formatMessage };
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const hour = parseInt(match[4], 10);
  const minute = granularity === DateTimeGranularity.Hour ? 0 : parseInt(match[5], 10);
  const second = granularity !== DateTimeGranularity.Second ? 0 : parseInt(match[6], 10);

  const dateResult = validateDateParts(year, month, day, options);
  if (!dateResult.valid) {
    return dateResult;
  }

  return validateTimeParts(hour, minute, second, { granularity, hourCycle });
};

export const validateDateTimeIsoString = (
  value: string,
  options: DateValidationOptions = {},
): DateTimeValidationResult => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(Z|([+-]\d{2}):(\d{2}))$/.exec(value);
  if (!match) {
    return {
      valid: false,
      message: 'Use format YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DDTHH:MM:SS±HH:MM.',
    };
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const hour = parseInt(match[4], 10);
  const minute = parseInt(match[5], 10);
  const second = parseInt(match[6], 10);

  const dateResult = validateDateParts(year, month, day, options);
  if (!dateResult.valid) {
    return dateResult;
  }

  const timeResult = validateTimeParts(hour, minute, second, {
    granularity: DateTimeGranularity.Second,
    hourCycle: 24,
  });
  if (!timeResult.valid) {
    return timeResult;
  }

  if (match[8] === 'Z') {
    return { valid: true };
  }

  const offsetHours = parseInt(match[9], 10);
  const offsetMinutes = parseInt(match[10], 10);

  if (Math.abs(offsetHours) > 23) {
    return { valid: false, message: 'Timezone offset hours must be between -23 and 23.' };
  }

  if (offsetMinutes < 0 || offsetMinutes > 59) {
    return { valid: false, message: buildRangeMessage('Timezone offset minutes', 0, 59) };
  }

  return { valid: true };
};

export const setDateByType = (date: Date, value: string, type: TimePickerType, period?: Period) => {
  switch (type) {
    case TimePickerType.Minutes:
      return setMinutes(date, value);
    case TimePickerType.Seconds:
      return setSeconds(date, value);
    case TimePickerType.Hours:
      return setHours(date, value);
    case TimePickerType.TwelveHours: {
      if (!period) {
        return date;
      }
      return set12Hours(date, value, period);
    }
    default:
      return date;
  }
};

export const getDateByType = (date: Date | null, type: TimePickerType) => {
  if (!date) {
    return '00';
  }
  switch (type) {
    case TimePickerType.Minutes:
      return getValidMinuteOrSecond(String(date.getMinutes()));
    case TimePickerType.Seconds:
      return getValidMinuteOrSecond(String(date.getSeconds()));
    case TimePickerType.Hours:
      return getValidHour(String(date.getHours()));
    case TimePickerType.TwelveHours:
      return getValid12Hour(String(display12HourValue(date.getHours())));
    default:
      return '00';
  }
};

export const getArrowByType = (value: string, step: number, type: TimePickerType) => {
  switch (type) {
    case TimePickerType.Minutes:
      return getValidArrowMinuteOrSecond(value, step);
    case TimePickerType.Seconds:
      return getValidArrowMinuteOrSecond(value, step);
    case TimePickerType.Hours:
      return getValidArrowHour(value, step);
    case TimePickerType.TwelveHours:
      return getValidArrow12Hour(value, step);
    default:
      return '00';
  }
};

/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
const convert12HourTo24Hour = (hour: number, period: Period) => {
  if (period === Period.PM) {
    if (hour <= 11) {
      return hour + 12;
    }
    return hour;
  }

  if (period === Period.AM) {
    if (hour === 12) {
      return 0;
    }
    return hour;
  }
  return hour;
};

/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
export const display12HourValue = (hours: number) => {
  if (hours === 0 || hours === 12) {
    return '12';
  }
  if (hours >= 22) {
    return `${hours - 12}`;
  }
  if (hours % 12 > 9) {
    return `${hours}`;
  }
  return `0${hours % 12}`;
};

export const genMonths = (locale: Pick<Locale, 'options' | 'localize' | 'formatLong'>) => {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: format(new Date(2021, i), 'MMMM', { locale }),
  }));
};

export const genYears = (fromDate?: Date, toDate?: Date) => {
  const defaultRange = getDefaultYearRange();

  const startYear = fromDate ? fromDate.getFullYear() : defaultRange.minYear;
  const endYear = toDate ? toDate.getFullYear() : defaultRange.maxYear;

  return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    return { value: year, label: year.toString() };
  });
};
